import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, CssBaseline, Box, Typography, CircularProgress } from "@mui/material";
import {
  AuthProvider,
  CryptoProvider,
  useAuth,
  AccessDeniedScreen,
  createAdminQueryClient,
  useAutoSelectValidRole,
  type Membership,
} from "@workspace/admin-ui";
import { theme } from "@/lib/theme";
import { ToastProvider } from "@/lib/toast";

import LoginPage from "@/pages/Login";
import DashboardPage from "@/pages/Dashboard";
import PharmaciesPage from "@/pages/Pharmacies";
import PharmacistsPage from "@/pages/Pharmacists";
import BookingsPage from "@/pages/Bookings";
import BookingsPharmacyDetailPage from "@/pages/BookingsPharmacyDetail";
import RoomsAvailabilityPage from "@/pages/RoomsAvailability";
import RoomsAvailabilityPharmacyDetailPage from "@/pages/RoomsAvailabilityPharmacyDetail";
import PharmacyDetailPage from "@/pages/PharmacyDetail";
import ConsultationsPage from "@/pages/Consultations";
import ConsultationDetailPage from "@/pages/ConsultationDetail";
import { AppShell } from "@/components/AppShell";

const queryClient = createAdminQueryClient();

const isChainAdmin = (m: Membership) =>
  m.status === "active" && m.role === "pharmacy_chain_admin" && !!m.chainId;

function Gate() {
  const { user, membership, loading } = useAuth();
  const auto = useAutoSelectValidRole(isChainAdmin);

  if (loading || (user && auto.switching)) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!user) return <LoginPage />;

  if (
    !membership || membership.status !== "active" || membership.role !== "pharmacy_chain_admin" || !membership.chainId
  ) {
    return <AccessDeniedScreen requiredRole="chain_admin" />;
  }

  return (
    <AppShell>
      <Switch>
        <Route path="/" component={DashboardPage} />
        <Route path="/pharmacies" component={PharmaciesPage} />
        <Route path="/pharmacists" component={PharmacistsPage} />
        <Route path="/bookings" component={BookingsPage} />
        <Route path="/bookings/:pharmacyId" component={BookingsPharmacyDetailPage} />
        <Route path="/rooms" component={RoomsAvailabilityPage} />
        <Route path="/rooms/:pharmacyId" component={RoomsAvailabilityPharmacyDetailPage} />
        <Route path="/pharmacies/:pharmacyId" component={PharmacyDetailPage} />
        <Route path="/consultations" component={ConsultationsPage} />
        <Route path="/consultations/:id" component={ConsultationDetailPage} />
        <Route>
          <Box sx={{ p: 4 }}>
            <Typography variant="h6">Not found</Typography>
          </Box>
        </Route>
      </Switch>
    </AppShell>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <AuthProvider panel="chain-admin">
          <CryptoProvider>
            <ToastProvider>
              <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
                <Gate />
              </WouterRouter>
            </ToastProvider>
          </CryptoProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
